import FVector from '@data/geometry/FVector';
import { getViewer } from 'store/viewerStore';
import * as THREE from 'three';
import { createMultiLineShape } from '../../Graphics/ShapeBuilder';

export class VisualEffects {
    private closeRadius: number;
    private closeRadiusIndicator: THREE.Mesh | null;
    private isShowingCloseIndicator: boolean;

    private spheres: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>[];
    private polyLine: THREE.Object3D | null;

    private basePoints: FVector[] = [];
    private hoveredSegmentIndex: number | null = null;

    private isEditing: boolean = false;

    constructor(closeRadius: number = 2.0) {
        this.closeRadius = closeRadius;
        this.closeRadiusIndicator = null;
        this.isShowingCloseIndicator = false;

        this.spheres = [];
        this.polyLine = null;
    }

    dispose() {
        this.hideCloseRadiusIndicator();
        if (this.closeRadiusIndicator) {
            getViewer().scene?.remove(this.closeRadiusIndicator);
            this.closeRadiusIndicator.geometry.dispose();
            (this.closeRadiusIndicator.material as THREE.Material).dispose();
            this.closeRadiusIndicator = null;
        }
        this.removePolyLine();
        this.removeSpheres();
    }

    // =====================================
    // CLOSE RADIUS INDICATOR
    // =====================================

    private createCloseRadiusIndicator(): void {
        const geometry = new THREE.RingGeometry(this.closeRadius - 0.1, this.closeRadius + 0.1, 32);
        const material = new THREE.MeshBasicMaterial({
            color: 0x00ff00,
            transparent: true,
            opacity: 0.7,
            side: THREE.DoubleSide,
        });

        this.closeRadiusIndicator = new THREE.Mesh(geometry, material);
        this.closeRadiusIndicator.visible = false;

        getViewer().scene?.add(this.closeRadiusIndicator);
    }

    public showCloseRadiusIndicator(position: FVector): void {
        if (!this.closeRadiusIndicator) this.createCloseRadiusIndicator();

        this.closeRadiusIndicator!.position.copy(position);
        this.closeRadiusIndicator!.visible = true;
        this.isShowingCloseIndicator = true;

        this.animateCloseIndicator();
    }

    public hideCloseRadiusIndicator(): void {
        if (this.closeRadiusIndicator) {
            this.closeRadiusIndicator.visible = false;
            this.isShowingCloseIndicator = false;
        }
    }

    private animateCloseIndicator(): void {
        if (!this.closeRadiusIndicator || !this.isShowingCloseIndicator) return;

        const time = Date.now() * 0.005;
        const scale = 1 + Math.sin(time) * 0.1;
        this.closeRadiusIndicator.scale.setScalar(scale);
        requestAnimationFrame(() => this.animateCloseIndicator());
    }

    // =====================================
    // SPHERE MANAGEMENT & HIGHLIGHTING
    // =====================================

    public createSphere(position: FVector, insertIndex?: number): THREE.Mesh {
        const sphere = new THREE.Mesh(
            new THREE.SphereGeometry(0.25, 16, 16),
            new THREE.MeshBasicMaterial({ color: 0x000000 }) // BLACK
        );

        sphere.position.copy(position);
        getViewer().scene?.add(sphere);

        if (insertIndex !== undefined && insertIndex >= 0 && insertIndex <= this.spheres.length) {
            this.spheres.splice(insertIndex, 0, sphere);
        } else {
            this.spheres.push(sphere);
        }

        // Reset base colors:
        this.updateEndpointColors();
        return sphere;
    }

    // Makes first and last spheres red and others black:
    public updateEndpointColors(): void {
        const count = this.spheres.length;
        if (count === 0) return;

        this.spheres.forEach((sphere) => {
            sphere.material.color.setHex(0x000000);
        });

        if (!this.isEditing) {
            this.spheres[0].material.color.setHex(0xff0000);
            this.spheres[count - 1]?.material.color.setHex(0xff0000);
        }
    }

    public removeSpheres(): void {
        this.spheres.forEach((s) => {
            getViewer().scene?.remove(s);
            s.geometry.dispose();
            (s.material as THREE.Material).dispose();
        });
        this.spheres = [];
    }

    public highlightSphere(index: number): void {
        // 0. Restore base colors:
        this.updateEndpointColors();

        // 1. Yellow hover:
        if (this.spheres[index]) {
            this.spheres[index].material.color.setHex(0xffff00);
        }
    }

    public highlightFirstSphere(): void {
        // 0. Restore base colors:
        this.updateEndpointColors();

        // 1. Yellow hover:
        if (this.spheres[0]) {
            this.spheres[0].material.color.setHex(0xffff00);
        }
    }

    public unhighlightAllSpheres(): void {
        this.updateEndpointColors();
    }

    public highlightClosedState(): void {
        this.spheres.forEach((sphere) => sphere.material.color.setHex(0x00ff00));
    }

    /**
     * Briefly shows a small red pulse sphere to indicate an invalid click.
     * Used when a point is outside the Polygon constraint.
     */
    public showInvalidClickIndicator(position: FVector): void {
        const geometry = new THREE.SphereGeometry(0.5, 16, 16);
        const material = new THREE.MeshBasicMaterial({
            color: 0xff0000,
            transparent: true,
            opacity: 0.8,
        });

        const sphere = new THREE.Mesh(geometry, material);
        sphere.position.copy(position);
        getViewer().scene?.add(sphere);

        // Optional: light pulse animation
        const startTime = performance.now();
        const duration = 600; // milliseconds

        const animate = (now: number) => {
            const elapsed = now - startTime;
            const t = elapsed / duration;

            if (t >= 1) {
                getViewer().scene?.remove(sphere);
                geometry.dispose();
                material.dispose();
                return;
            }

            // simple fade + pulse
            const scale = 1 + Math.sin(t * Math.PI) * 0.5;
            sphere.scale.setScalar(scale);
            material.opacity = 0.8 * (1 - t);

            requestAnimationFrame(animate);
        };

        requestAnimationFrame(animate);
    }

    // =====================================
    // POLYLINE RENDERING
    // =====================================

    sanitize = (pts: FVector[]) =>
        pts.filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z)).map((p) => new FVector(p.x, p.y, p.z));
    public generatePolyLine(points: FVector[]): void {
        points = this.sanitize(points);
        if (!points || points.length < 2) return;
        this.basePoints = points.map((p) => p.clone());
        this.hoveredSegmentIndex = null;

        this.rebuildPolyLine();
    }

    private rebuildPolyLine(): void {
        if (!this.basePoints.length) return;

        // 1. Remove existing polyline:
        this.removePolyLine();

        // 2. Populate vertices and colors array:
        const vertices: number[] = [];
        const colors: number[] = [];

        for (let i = 0; i < this.basePoints.length - 1; i++) {
            const a = this.basePoints[i];
            const b = this.basePoints[i + 1];

            // 2.1. Vertices:
            vertices.push(a.x, a.y, a.z, b.x, b.y, b.z);
            const isHovered = this.hoveredSegmentIndex === i;

            // 2.2. Yellow highlight if a segment is hovered:
            const color = isHovered ? new THREE.Color(0xffff00) : new THREE.Color(0x000000);
            colors.push(color.r, color.g, color.b, color.r, color.g, color.b);
        }

        // 3. Generate and add polyline to scene:
        const vertexArray = new Float32Array(vertices);
        this.polyLine = createMultiLineShape(vertexArray, colors, 2);
        getViewer().scene?.add(this.polyLine);
    }

    public removePolyLine(): void {
        if (this.polyLine) {
            getViewer().scene?.remove(this.polyLine);
            this.polyLine.traverse((child) => {
                const mesh = child as THREE.Mesh;
                const geometry = (mesh as any).geometry;
                if (geometry?.dispose) geometry.dispose();
                const material = (mesh as any).material;
                if (Array.isArray(material)) {
                    material.forEach((mat) => mat?.dispose?.());
                } else {
                    material?.dispose?.();
                }
            });
            this.polyLine = null;
        }
    }

    public highlightSegment(index: number): void {
        if (index < 0 || index >= this.basePoints.length - 1) return;

        this.hoveredSegmentIndex = index;
        this.rebuildPolyLine();
    }

    public clearSegmentHighlight(): void {
        if (this.hoveredSegmentIndex === null) return;

        this.hoveredSegmentIndex = null;
        this.rebuildPolyLine();
    }

    // =====================================
    // GETTERS AND SETTERS
    // =====================================

    getSpheres(): THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>[] {
        return this.spheres;
    }

    setIsEditing(isEditing: boolean) {
        this.isEditing = isEditing;
    }
}
